import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, ArrowRight, ArrowLeft } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext.js";
import { useFavorites } from "../../../contexts/FavoritesContext.js";
import api from "../../../api/client.js";
import ProductCard from "../../ProductCard/ProductCard";
import logger from "../../../utils/logger.js";
import Breadcrumb from "../../../components/ui/Breadcrumb/Breadcrumb";
import Pagination from "../../../components/ui/Pagination/Pagination";
import "./Favorites.css";

function FallingHearts({ count = 40, active = true }) {
  const hearts = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${2 + (i * 96) / (count - 1)}%`,
      delay: `${(i % 10) * 0.12 + Math.floor(i / 10) * 0.4}s`,
      size: `${14 + Math.random() * 22}px`,
      duration: `${2.5 + Math.random() * 2}s`,
      sway: `${-30 + Math.random() * 60}px`,
    }));
  }, [count]);

  if (!active) return null;

  return (
    <div className="falling-hearts-container" aria-hidden="true">
      {hearts.map((h) => (
        <div
          key={h.id}
          className="falling-heart"
          style={{
            left: h.left,
            animationDelay: h.delay,
            width: h.size,
            height: h.size,
            "--sway": h.sway,
            animationDuration: h.duration,
          }}
        >
          <Heart size="100%" fill="#dc2626" stroke="none" />
        </div>
      ))}
    </div>
  );
}

function getItemsPerPage() {
  const width = window.innerWidth;
  if (width > 1200) return 12;
  if (width > 1024) return 12;
  if (width > 768) return 9;
  return 8;
}

export default function Favorites() {
  const { lang } = useLanguage();
  const { favorites, loading: favoritesLoading } = useFavorites();
  const isAr = lang === "ar";


  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [msg, setMsg] = useState("");
  const [showFallingHearts, setShowFallingHearts] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => getItemsPerPage());

  const lastFetchedKeyRef = useRef("");
  const animatingCountRef = useRef(0);
  const favoritesKey = useMemo(() => favorites.map(f => f?.id).join(','), [favorites]);

  useEffect(() => {
    if (products.length > 0 && !loadingProducts && !favoritesLoading) {
      const timer = setTimeout(() => setShowFallingHearts(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [products.length, loadingProducts, favoritesLoading]);

  useEffect(() => {
    function handleResize() {
      const newItemsPerPage = getItemsPerPage();
      setItemsPerPage((prev) => {
        if (prev !== newItemsPerPage) {
          setCurrentPage(1);
          return newItemsPerPage;
        }
        return prev;
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let isMounted = true;
    let loadingTimer = null;

    async function loadFavoriteProducts() {
      if (!isMounted) return;

      if (!favorites.length) {
        setProducts([]);
        setMsg("");
        setLoadingProducts(false);
        lastFetchedKeyRef.current = "";
        return;
      }

      if (favoritesKey === lastFetchedKeyRef.current && products.length > 0) {
        return;
      }

      if (animatingCountRef.current > 0) {
        lastFetchedKeyRef.current = favoritesKey;
        return;
      }

      loadingTimer = setTimeout(() => {
        if (isMounted) setLoadingProducts(true);
      }, 100);

      try {
        setMsg("");

        const filteredProducts = favorites.map((fav) => fav.raw || fav).filter(Boolean);

        if (!isMounted) return;

        setProducts(filteredProducts);
        lastFetchedKeyRef.current = favoritesKey;

      } catch (error) {
        logger.error("Error loading favorite products", error);
        if (!isMounted) return;
        setProducts([]);
        setMsg(
          isAr
            ? "تعذر تحميل المنتجات. حاول لاحقاً."
            : "Unable to load products. Try again later."
        );
      } finally {
        if (loadingTimer) clearTimeout(loadingTimer);
        if (isMounted) {
          setLoadingProducts(false);
        }
      }
    }

    loadFavoriteProducts();

    return () => {
      isMounted = false;
      if (loadingTimer) clearTimeout(loadingTimer);
    };
  }, [favorites, favoritesLoading, isAr, products.length]);

  const breadcrumbItems = [
    { label: isAr ? "المتجر" : "Shop", link: "/shop" },
    { label: isAr ? "المفضلة" : "Favorites" },
  ];

  const isLoading = (favoritesLoading || loadingProducts) && products.length === 0;

  const totalPages = Math.ceil(products.length / itemsPerPage);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return products.slice(start, start + itemsPerPage);
  }, [products, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleRemoveFromFavorites = useCallback((productId) => {
    const id = String(productId);
    animatingCountRef.current += 1;

    setProducts((prev) =>
      prev.map((p) =>
        String(p?.id) === id ? { ...p, __exiting: true } : p
      )
    );

    setTimeout(() => {
      setProducts((prev) => prev.filter((p) => String(p?.id) !== id));
      animatingCountRef.current = Math.max(0, animatingCountRef.current - 1);
    }, 400);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const ArrowIcon = isAr ? ArrowRight : ArrowLeft;

  if (isLoading) {
    return (
      <div className="favorites-page" dir={isAr ? "rtl" : "ltr"}>
        <FallingHearts count={50} active={true} />
        <div className="favorites-header">
          <div className="favorites-header-content">
            <div className="favorites-title-wrapper">
              <div className="favorites-header-icon">
                <Heart size={24} fill="#dc2626" stroke="none" />
              </div>
              <h1 className="favorites-header-title">
                {isAr ? "المفضلة" : "Favorites"}
              </h1>
            </div>
          </div>
        </div>
        <div className="favorites-container">
          <div className="favorites-empty" style={{ paddingTop: "120px" }}>
            <div className="favorites-empty-icon">
              <Heart size={48} fill="#dc2626" stroke="none" />
            </div>
            <h2>{isAr ? "جاري التحميل..." : "Loading..."}</h2>
            <p>
              {isAr
                ? "نحن نجمع منتجاتك المفضلة..."
                : "Gathering your favorite items..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-page" dir={isAr ? "rtl" : "ltr"}>
      <FallingHearts count={35} active={showFallingHearts} />

      <Breadcrumb items={breadcrumbItems} mobileOnly />

      <header className="favorites-header">
        <div className="favorites-header-content">
          <div className="favorites-title-wrapper">
            <div className="favorites-header-icon">
              <Heart size={24} fill="#dc2626" stroke="none" />
            </div>
            <h1 className="favorites-header-title">
              {isAr ? "المفضلة" : "Favorites"}
            </h1>
          </div>
        </div>
      </header>

      <div className="favorites-container">
        <div className="favorites-subheader">
          <p className="favorites-subheader-text">
            {isAr
              ? "منتجاتك المفضلة في مكان واحد."
              : "Your favorite items, all in one place."}
          </p>
        </div>

        {msg && (
          <div className="favorites-error">
            <span>{msg}</span>
          </div>
        )}

        {products.length === 0 ? (
          <div className="favorites-empty">
            <div className="favorites-empty-icon">
              <Heart size={48} fill="#dc2626" stroke="none" />
            </div>
            <h2>{isAr ? "لا توجد منتجات" : "No items yet"}</h2>
            <p>
              {isAr
                ? "اضغط القلب لحفظ منتجاتك المفضلة."
                : "Tap the heart to save your favorite items."}
            </p>
            <Link to="/shop" className="favorites-btn-primary">
              <ShoppingBag size={18} />
              {isAr ? "تصفح المتجر" : "Browse Shop"}
            </Link>
          </div>
        ) : (
          <>
            <section className="favorites-grid-section">
              <div className="favorites-grid">
                {paginatedProducts.map((product, index) => {
                  const isExiting = product.__exiting === true;
                  return (
                    <article
                      key={product?.id ?? `product-${index}`}
                      className={`favorites-tile ${isExiting ? "is-exiting" : ""}`}
                      style={{ "--i": index }}
                    >
                      <ProductCard 
                        product={product} 
                        onRemoveFromFavorites={() => handleRemoveFromFavorites(product?.id)}
                      />
                    </article>
                  );
                })}
              </div>
            </section>
            <div className="favorites-pagination-row">
              <Link to="/shop" className="favorites-btn-outline">
                <ShoppingBag size={18} />
                {isAr ? "العودة إلى المتجر" : "Back to shop"}
                <ArrowIcon size={16} />
              </Link>
              {totalPages > 1 && (
                <div className="pagination-wrapper">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}